import { useState, useCallback, FC } from 'react';

import cn from 'classnames';
import { FileRejection, useDropzone } from 'react-dropzone';
import { Button } from 'ui/components/common';
import { toastError, toastWarning } from 'ui/partials/Toast';
import useModal from 'ui/partials/Modal/useModal';
import { useUploadToUploadCare } from 'utils/helpers';
import { useFormikContext } from 'formik';
import { UPLOAD_ATTACHMENTS_FORM } from 'modules/account/contact/forms/schema';

import UploadList from './UploadList';

type DraggableFileUploaderProps = {
  label?: string;
  id?: string;
  disabled?: boolean;
  onSetFieldValue?: string;
  sizePerFileLimit: {
    size: number;
    message: string;
  };
  removeFileSizeLimit?: boolean;
  source?:
    | 'Comment'
    | 'Lead'
    | 'Company'
    | 'Task'
    | 'OrderInvoice'
    | 'Deal'
    | 'AICaller';
  referenceId: string;
  singleFile?: boolean;
};

const DraggableFileUploader: FC<DraggableFileUploaderProps> = ({
  id = 'uploaded_photo',
  disabled = false,
  removeFileSizeLimit,
  sizePerFileLimit,
  source,
  referenceId,
  singleFile,
  ...rest
}) => {
  const { hideModal } = useModal();
  const [isUploading, setUploading] = useState(false);
  const { mutateAsync: uploadFiles, progressCount } = useUploadToUploadCare();

  const { values, setFieldValue, handleSubmit } =
    useFormikContext<UPLOAD_ATTACHMENTS_FORM>();

  const filesUploaded = values.files;

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (!acceptedFiles.length && rejectedFiles.length) {
        if (rejectedFiles[0].errors[0].code === 'file-too-large') {
          return toastError(sizePerFileLimit.message);
        }
        return toastError(rejectedFiles[0].errors[0].message);
      }

      setUploading(true);

      const result = await uploadFiles(acceptedFiles, {
        onSettled: () => {
          setUploading(false);
        },
      });

      setFieldValue('files', [
        ...values.files,
        ...result.files.map((file) => ({
          source,
          referenceId,
          fileUrl: file.cdnUrl,
          ...(file.mimeType?.startsWith('image') && {
            fileThumbnail: file.cdnUrl,
          }),
          fileName: file.originalFilename,
          fileSize: file.size,
          fileType: file.mimeType,
          id: file.uuid,
        })),
      ]);

      if (rejectedFiles.length) {
        return toastWarning(
          `${acceptedFiles.length} files uploaded. ${rejectedFiles.length} files failed to upload`,
        );
      }
    },
    [
      referenceId,
      setFieldValue,
      sizePerFileLimit.message,
      source,
      uploadFiles,
      values.files,
    ],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: isUploading,
    noKeyboard: isUploading,
    noDrag: isUploading,
    maxSize: removeFileSizeLimit ? 0 : sizePerFileLimit.size,
    multiple: !singleFile,
    maxFiles: singleFile ? 1 : undefined,
  });

  const handleDelete = (_id: string) => {
    setFieldValue(
      'files',
      values.files.filter((file) => file.id !== _id),
    );
  };

  return (
    <form onSubmit={handleSubmit} className="pt-2.5">
      <div className="flex flex-wrap justify-center">
        <div
          {...(isUploading ||
          disabled ||
          (singleFile && !!filesUploaded?.length)
            ? {}
            : getRootProps())}
        >
          <input {...getInputProps()} />
          <Button
            id={id}
            label={isUploading ? 'UPLOADING' : 'UPLOAD FILE'}
            intent={'tertiary'}
            size={'lg'}
            {...rest}
            className="mx-auto"
            disabled={
              isUploading || disabled || (singleFile && !!filesUploaded?.length)
            }
          />
          <div
            className={cn(
              'leading-[24px]',
              'tracking-[0.04em]',
              'text-content',
              'mt-[10px]',
              'w-full',
              'text-center',
            )}
          >
            Or drag your file/s here
          </div>
        </div>
        {(!!filesUploaded?.length || isUploading) && (
          <UploadList
            progressCount={progressCount}
            isUploading={isUploading}
            onDelete={handleDelete}
            list={filesUploaded}
          />
        )}
      </div>
      <div
        className={cn(
          'flex justify-end gap-3',
          'px-2.5 pt-5 mt-5',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          type="submit"
          disabled={!filesUploaded?.length || disabled}
          label="SUBMIT"
        />
        <Button intent={'tertiary'} onClick={hideModal} label="CANCEL" />
      </div>
    </form>
  );
};

export default DraggableFileUploader;
